/**
 * RebarSweep.ts
 * -----------------------------------------------------------------------------
 * Mitred swept tubes for reinforcement-bar and tendon/duct centrelines.
 *
 * The cheap way to draw a bar is one cylinder per straight leg, which is what
 * BridgeScene's instanced path does. Each cylinder is cut by a plane
 * perpendicular to ITS OWN axis, so two legs meeting at a turn angle t
 * interpenetrate inside the bend and leave 2*r*sin(t/2) of nothing outside it —
 * 1.7 mm per vertex on a 65 mm duct at 3 degree steps (a scalloped line of
 * background down the outside of every curve), 35 mm at a 90 degree corner on a
 * 25 mm bar.
 *
 * This module sweeps instead: ONE ring per polyline VERTEX, lying in the plane
 * that bisects the two legs meeting there, shared by both quad strips. No gap,
 * no shading break, and the same 2*seg triangles a segment the cylinders cost.
 *
 * The algorithm — run splitting, the mitre normal, and the rotation-minimising
 * frame — is the one in CSiBKK.Components.Core/Engine/Rebar/RebarMeshEngine.cs
 * (SweepTube / Tangent / SeedFrame / CarryFrame), and the names here match so
 * the two can be diffed. There is ONE deliberate divergence, the mitre ellipse:
 * see mitreSlide below. RebarMeshEngine puts a plain circle of radius r in the
 * mitre plane, which necks the tube to r*cos(t/2) — 29% at a 90 degree corner.
 * That is a waist where this had a notch, so it is not worth copying; porting
 * the correction back into the C# is a separate change, because the U-girder
 * mesh tests assert on widths it would move.
 *
 * Nothing here allocates per vertex: the vector maths runs through module
 * scratch, the per-vertex arrays grow geometrically and are reused, and the
 * caller owns the output buffers.
 */

import * as THREE from 'three';

/**
 * Cosine of the largest turn a mitre is built for (135 degrees).
 *
 * As the turn approaches 180 the mitre plane goes parallel to the legs, the
 * ellipse degenerates into an infinite strip and the slide below diverges. At
 * 135 the ring is already 2.61*r long and the mitre reaches 2.41*r past the
 * vertex — a blade, not a joint. Tested on the dot product, so no acos is ever
 * needed.
 */
export const MITRE_MIN_LEG_DOT = -0.7071067811865476;

/** Legs shorter than this (metres) are dropped, matching the coincident-endpoint
 *  test in BridgeScene's instanced path. */
export const MIN_LEG = 1e-5;

/**
 * Rib pitch as a fraction of bar DIAMETER - the spacing of the transverse ribs along
 * the bar. 0.5 is mid-range for hot-rolled deformed bar (BS 4449 / ASTM A615 put it
 * at roughly 0.5 to 0.7), and being a fraction of the diameter it is right at every
 * bar size rather than at one.
 *
 * The only scale the surface pattern needs, because the texture maps EXACTLY ONCE
 * around the tube - see the uvs remark on {@link SweepOut}.
 */
export const RIB_PITCH_FACTOR = 0.5;

/** first ~ last => a closed loop (a tie, a stirrup). 0.1 mm: the payload carries
 *  float metres converted from the same millimetre value at both ends, so the two
 *  are bit-identical in practice, and no two ends of ONE bar are 0.1 mm apart. */
export const LOOP_EPS = 1e-4;

/** Exact buffer footprint of a sweep. */
export interface SweepSize {
  vertices: number;
  triangles: number;
}

/** Destination buffers plus the write cursors — `v` counts vertices (not floats),
 *  `i` counts index slots. */
export interface SweepOut {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  /**
   * `u` in TURNS around the tube - exactly ONE per circuit - and `v` in rib pitches
   * of {@link RIB_PITCH_FACTOR} travelled along it. The material needs no `repeat`.
   *
   * One turn per circuit is what lets the pattern place a fixed number of LONGITUDINAL
   * ribs on the bar: the generated map draws two spines, and two is what appears.
   * Anything tiling more than once per circuit multiplies them - an earlier version
   * put five tiles round a bar, so ten spines, and read as basket-weave rather than as
   * reinforcement.
   *
   * Nothing here is square, and it does not need to be: the pattern is GENERATED, so
   * it knows the aspect ratio (circumference over rib pitch, a constant pi divided by
   * RIB_PITCH_FACTOR) and draws the rib lean correctly within it. That is the whole
   * advantage a drawn pattern has over a photograph, which can only be stretched.
   *
   * Both axes still meet themselves: `u` wraps at a whole turn with a spine sitting on
   * that seam, and `v` is periodic in one pitch.
   *
   * Present only when the caller asked for UVs — see `withUv` on
   * {@link sweepPolyline}. Absent, a ring is `seg` vertices; present, it is
   * `seg + 1`, because the seam vertex needs `u` to reach the far edge of the
   * texture instead of wrapping to 0 and mirroring the whole tile into one facet.
   */
  uvs: Float32Array | null;
  v: number;
  i: number;
}

// ---------------------------------------------------------------------------
// Scratch. Module-level so a million-segment build allocates nothing per vertex,
// the same trade BridgeScene's instanced loop makes with its Vector3/Matrix4 set.
// ---------------------------------------------------------------------------

const A = new THREE.Vector3(); // incoming leg, unit
const B = new THREE.Vector3(); // outgoing leg, unit
const M = new THREE.Vector3(); // mitre-plane normal
const REF = new THREE.Vector3(); // leg the ring circle is built perpendicular to
const U = new THREE.Vector3(); // transported reference axis, carried ring to ring
const CU = new THREE.Vector3();
const CV = new THREE.Vector3();
const RP = new THREE.Vector3(); // ring basis 1, perpendicular to REF
const RQ = new THREE.Vector3(); // ring basis 2
const W = new THREE.Vector3(); // unit radial
const D = new THREE.Vector3(); // general scratch
const N = new THREE.Vector3(); // vertex normal
const C = new THREE.Vector3(); // ring centre
const D0 = new THREE.Vector3(); // axis at a run's first ring
const D1 = new THREE.Vector3(); // axis at a run's last ring
const PREV = new THREE.Vector3(); // previous ring centre, for the arc-length v
const SEED = new THREE.Vector3();
const RESEED = new THREE.Vector3();

/** Per-vertex tan(turn/2), zero where the vertex is not mitred. */
let tanHalf = new Float64Array(0);
/** Per-vertex flag: 1 where the two legs meeting there get a shared mitred ring. */
let mitre = new Uint8Array(0);
/** Vertex visiting order; a run break appears twice, so this needs n+1 slots. */
let order = new Int32Array(0);

function ensureVertexScratch(n: number): void {
  if (tanHalf.length >= n) return;
  const size = Math.max(n, tanHalf.length * 2, 64);
  tanHalf = new Float64Array(size);
  mitre = new Uint8Array(size);
  order = new Int32Array(size + 1);
}

/** cos/sin of the ring angles, rebuilt only when the facet count changes. */
let circleSeg = -1;
let circleCos = new Float64Array(0);
let circleSin = new Float64Array(0);

function ensureCircle(seg: number): void {
  if (circleSeg === seg) return;
  circleCos = new Float64Array(seg);
  circleSin = new Float64Array(seg);
  for (let k = 0; k < seg; k++) {
    const angle = (2 * Math.PI * k) / seg;
    circleCos[k] = Math.cos(angle);
    circleSin[k] = Math.sin(angle);
  }
  circleSeg = seg;
}

// ---------------------------------------------------------------------------
// Frames — ports of RebarMeshEngine.CarryFrame / SeedFrame.
// ---------------------------------------------------------------------------

/**
 * Rotation-minimising step: project the previous ring's reference axis onto the
 * new ring plane, so the quad strip does not twist between rings. `outU` may
 * alias `previousU`.
 */
function carryFrame(
  previousU: THREE.Vector3,
  tangent: THREE.Vector3,
  outU: THREE.Vector3,
  outV: THREE.Vector3,
): void {
  const dot = previousU.dot(tangent);
  outU.set(
    previousU.x - dot * tangent.x,
    previousU.y - dot * tangent.y,
    previousU.z - dot * tangent.z,
  );
  const len = outU.length();
  if (len < 1e-9) {
    // Previous axis collapsed onto the tangent — reseed from a different
    // reference. Never recurses twice: neither fallback can be parallel to the
    // tangent that rejected the other.
    const useX = Math.abs(tangent.x) < 0.9;
    RESEED.set(useX ? 1 : 0, useX ? 0 : 1, 0);
    carryFrame(RESEED, tangent, outU, outV);
    return;
  }
  outU.divideScalar(len);
  outV.crossVectors(tangent, outU);
}

/** Orthonormal pair spanning the plane perpendicular to `tangent`. */
function seedFrame(tangent: THREE.Vector3, outU: THREE.Vector3, outV: THREE.Vector3): void {
  SEED.set(0, 0, 1);
  if (Math.abs(tangent.z) >= 0.9) SEED.set(1, 0, 0);
  carryFrame(SEED, tangent, outU, outV);
}

// ---------------------------------------------------------------------------
// Polyline access. `idx[k]` is the FLOAT offset of kept vertex k inside `pos`,
// so nothing is copied out of the payload.
// ---------------------------------------------------------------------------

function point(pos: ArrayLike<number>, idx: Int32Array, i: number, out: THREE.Vector3): void {
  const o = idx[i];
  out.set(pos[o], pos[o + 1], pos[o + 2]);
}

/** Unit direction from vertex `i` to vertex `j`; returns the leg's length. */
function leg(
  pos: ArrayLike<number>,
  idx: Int32Array,
  i: number,
  j: number,
  out: THREE.Vector3,
): number {
  const a = idx[i];
  const b = idx[j];
  out.set(pos[b] - pos[a], pos[b + 1] - pos[a + 1], pos[b + 2] - pos[a + 2]);
  const len = out.length();
  if (len > 0) out.divideScalar(len);
  return len;
}

/**
 * Drop consecutive duplicates. `idx[k]` receives the float offset of kept vertex
 * k; no coordinates are copied. Returns the kept count.
 */
export function dedupePolyline(
  pos: ArrayLike<number>,
  base: number,
  count: number,
  idx: Int32Array,
): number {
  let n = 0;
  let px = 0;
  let py = 0;
  let pz = 0;
  for (let k = 0; k < count; k++) {
    const o = base + k * 3;
    const x = pos[o];
    const y = pos[o + 1];
    const z = pos[o + 2];
    if (n > 0) {
      const dx = x - px;
      const dy = y - py;
      const dz = z - pz;
      if (dx * dx + dy * dy + dz * dz < MIN_LEG * MIN_LEG) continue;
    }
    idx[n++] = o;
    px = x;
    py = y;
    pz = z;
  }
  return n;
}

/**
 * True when the deduped polyline closes on itself. The caller then drops the
 * last vertex and sweeps the rest as a loop, as RebarMeshEngine.SweepTube does.
 */
export function isClosedLoop(pos: ArrayLike<number>, idx: Int32Array, n: number): boolean {
  if (n <= 3) return false;
  const a = idx[0];
  const b = idx[n - 1];
  const dx = pos[a] - pos[b];
  const dy = pos[a + 1] - pos[b + 1];
  const dz = pos[a + 2] - pos[b + 2];
  return dx * dx + dy * dy + dz * dz < LOOP_EPS * LOOP_EPS;
}

// ---------------------------------------------------------------------------
// The sweep.
// ---------------------------------------------------------------------------

/** Accumulator shared by the run emitter, so a run costs no allocation. */
const ACC: SweepSize = { vertices: 0, triangles: 0 };

/**
 * Distance to slide a ring vertex along the reference leg to land it on the
 * mitre plane, which is what makes the joint exact.
 *
 * With the circle built perpendicular to the incoming leg `a` (so w.a == 0) and
 * `m` the unit bisector of the two legs,
 *
 *     t = -r * (w.m) / (a.m)        and      a.m = b.m = cos(turn/2)
 *
 * The slid point stays at perpendicular distance r from the incoming axis
 * (sliding along an axis cannot change that), and it lands in the mitre plane —
 * which is the reflection plane swapping the two leg axes, so every point of it
 * is equidistant from both. The vertex is therefore exactly on BOTH legs'
 * cylinders: watertight, and at full diameter rather than the r*cos(turn/2) a
 * plain circle in that plane would give.
 */
function mitreSlide(radius: number): number {
  const denom = REF.dot(M);
  // cos(turn/2) >= cos(67.5) = 0.38 wherever a mitre was allowed, and exactly 1
  // at a run end (where m == ref, so w.m is 0 and the slide vanishes anyway).
  return denom > 1e-6 ? (-radius * W.dot(M)) / denom : 0;
}

/**
 * Emit one run of rings — `ord[s..e]`, inclusive. `loop` closes the last ring
 * back onto the first (and suppresses caps, a loop having no ends).
 *
 * Sizes are accumulated into {@link ACC} whether or not `out` is present, and
 * they depend only on the run's shape, so the measuring pass can never disagree
 * with the writing one.
 */
function emitRun(
  pos: ArrayLike<number>,
  idx: Int32Array,
  ord: Int32Array,
  s: number,
  e: number,
  loop: boolean,
  radius: number,
  seg: number,
  capped: boolean,
  withUv: boolean,
  out: SweepOut | null,
): void {
  const rings = e - s + 1;
  if (rings < 2) return;

  // A textured ring carries a seam vertex so `u` can reach the tile edge; an
  // untextured one closes on itself. Taken from the PARAMETER and not from
  // `out`, so the measuring pass sizes the buffers the writer will actually fill.
  const stride = withUv ? seg + 1 : seg;
  const strips = loop ? rings : rings - 1;
  const caps = capped && !loop;
  ACC.vertices += rings * stride + (caps ? 2 * seg : 0);
  ACC.triangles += strips * seg * 2 + (caps ? 2 * (seg - 2) : 0);
  if (!out) return;

  const base = out.v;
  const positions = out.positions;
  const normals = out.normals;
  const indices = out.indices;
  const uvs = out.uvs;

  // Distance travelled along this run, which is the `v` coordinate.
  let along = 0;
  // v is measured in rib pitches, a fraction of the DIAMETER, so rib spacing scales
  // with the bar. Guarded so a degenerate radius cannot divide by zero.
  const ribPitch = Math.max(RIB_PITCH_FACTOR * 2 * radius, 1e-6);

  for (let j = s; j <= e; j++) {
    const vi = ord[j];
    const prevPos = j > s ? j - 1 : loop ? e : -1;
    const nextPos = j < e ? j + 1 : loop ? s : -1;

    if (prevPos >= 0) leg(pos, idx, ord[prevPos], vi, A);
    if (nextPos >= 0) leg(pos, idx, vi, ord[nextPos], B);

    if (prevPos >= 0 && nextPos >= 0) {
      // The mitre normal is the average of the legs arriving and leaving —
      // RebarMeshEngine.Tangent.
      M.copy(A).add(B);
      const ml = M.length();
      if (ml < 1e-9) M.copy(B);
      else M.divideScalar(ml);
      REF.copy(A);
    } else if (prevPos >= 0) {
      M.copy(A);
      REF.copy(A);
    } else {
      M.copy(B);
      REF.copy(B);
    }

    // Carry the master axis against the MITRE normals, as SweepTube does, then
    // derive the ring basis against the reference leg. Carrying against the
    // bisector splits the small phase shear between the two legs instead of
    // loading it all onto one of them.
    if (j === s) seedFrame(M, U, CV);
    else {
      carryFrame(U, M, CU, CV);
      U.copy(CU);
    }
    carryFrame(U, REF, RP, RQ);

    if (j === s) D0.copy(B);
    if (j === e) D1.copy(A);

    point(pos, idx, vi, C);
    const interior = prevPos >= 0 && nextPos >= 0;

    // Arc length so far, for `v`. Measured on the CENTRELINE rather than on the
    // swept surface: a bar's texture should advance with the bar, not stretch on
    // the outside of a bend and bunch on the inside.
    if (j > s) along += C.distanceTo(PREV);
    PREV.copy(C);
    const vTiles = along / ribPitch;

    for (let k = 0; k < stride; k++) {
      const kc = k === seg ? 0 : k; // the seam vertex repeats angle 0
      const ca = circleCos[kc];
      const sa = circleSin[kc];
      W.set(RP.x * ca + RQ.x * sa, RP.y * ca + RQ.y * sa, RP.z * ca + RQ.z * sa);
      const t = mitreSlide(radius);
      const vx = C.x + radius * W.x + t * REF.x;
      const vy = C.y + radius * W.y + t * REF.y;
      const vz = C.z + radius * W.z + t * REF.z;

      if (interior) {
        // Bisector of the two cylinders' normals: the vertex is on both, so this
        // is the smooth-shaded crease normal, and the ONE shared vertex carries
        // it — no shading break at the joint, and no computeVertexNormals pass.
        D.set(vx - C.x, vy - C.y, vz - C.z);
        const db = D.dot(B);
        N.set(D.x - db * B.x, D.y - db * B.y, D.z - db * B.z);
        const nl = N.length();
        if (nl > 1e-12) N.divideScalar(nl);
        else N.copy(W);
        N.add(W);
        const nl2 = N.length();
        if (nl2 > 1e-12) N.divideScalar(nl2);
        else N.copy(W);
      } else {
        N.copy(W);
      }

      const o = out.v * 3;
      positions[o] = vx;
      positions[o + 1] = vy;
      positions[o + 2] = vz;
      normals[o] = N.x;
      normals[o + 1] = N.y;
      normals[o + 2] = N.z;
      if (uvs) {
        const uo = out.v * 2;
        // u in TURNS, exactly one per circuit; v in rib pitches. No shear here: the
        // longitudinal ribs of hot-rolled bar are straight, and the pattern draws
        // whatever lean the transverse ribs need itself. See SweepOut.uvs.
        uvs[uo] = k / seg;
        uvs[uo + 1] = vTiles;
      }
      out.v++;
    }
  }

  // Barrel. Neighbouring strips share their ring, which is the whole point; the
  // modulo only bites on a loop.
  for (let r = 0; r < strips; r++) {
    const ringA = base + r * stride;
    const ringB = base + ((r + 1) % rings) * stride;
    for (let k = 0; k < seg; k++) {
      // With a seam vertex k+1 is always in range; without one the last facet
      // closes back onto vertex 0.
      const k1 = withUv ? k + 1 : (k + 1) % seg;
      indices[out.i++] = ringA + k;
      indices[out.i++] = ringA + k1;
      indices[out.i++] = ringB + k1;
      indices[out.i++] = ringA + k;
      indices[out.i++] = ringB + k1;
      indices[out.i++] = ringB + k;
    }
  }

  if (!caps) return;

  // A cap vertex needs the axial normal and a vertex carries one normal, so each
  // cap duplicates its ring. Terminal rings are plain circles (the slide is zero
  // there), so the cap is planar and a fan from corner 0 covers it — no centre
  // vertex, seg-2 triangles.
  const startRing = base;
  const endRing = base + (rings - 1) * stride;

  const capStart = out.v;
  for (let k = 0; k < seg; k++) {
    const src = (startRing + k) * 3;
    const o = out.v * 3;
    positions[o] = positions[src];
    positions[o + 1] = positions[src + 1];
    positions[o + 2] = positions[src + 2];
    normals[o] = -D0.x;
    normals[o + 1] = -D0.y;
    normals[o + 2] = -D0.z;
    if (uvs) {
      // A cap is a flat disc a few millimetres across at the very end of a bar.
      // Map its own circle into one tile rather than trying to be world-scale:
      // there is nothing for it to line up with, and it keeps the cap off the seam.
      const uo = out.v * 2;
      uvs[uo] = 0.5 + 0.5 * circleCos[k];
      uvs[uo + 1] = 0.5 + 0.5 * circleSin[k];
    }
    out.v++;
  }
  for (let k = 1; k <= seg - 2; k++) {
    indices[out.i++] = capStart;
    indices[out.i++] = capStart + k + 1;
    indices[out.i++] = capStart + k;
  }

  const capEnd = out.v;
  for (let k = 0; k < seg; k++) {
    const src = (endRing + k) * 3;
    const o = out.v * 3;
    positions[o] = positions[src];
    positions[o + 1] = positions[src + 1];
    positions[o + 2] = positions[src + 2];
    normals[o] = D1.x;
    normals[o + 1] = D1.y;
    normals[o + 2] = D1.z;
    if (uvs) {
      const uo = out.v * 2;
      uvs[uo] = 0.5 + 0.5 * circleCos[k];
      uvs[uo + 1] = 0.5 + 0.5 * circleSin[k];
    }
    out.v++;
  }
  for (let k = 1; k <= seg - 2; k++) {
    indices[out.i++] = capEnd;
    indices[out.i++] = capEnd + k;
    indices[out.i++] = capEnd + k + 1;
  }
}

/**
 * Mitred swept tube for ONE polyline.
 *
 * `out === null` measures only. The joint arithmetic that decides where a run
 * has to break runs in both modes — only the ring and index writes are skipped —
 * so a measured size can never disagree with what the writer then emits.
 *
 * `n` is the vertex count to use and `closed` says the polyline returns to its
 * start: the caller passes `n - 1` with `closed` true, having found the last
 * vertex to be a repeat of the first.
 */
export function sweepPolyline(
  pos: ArrayLike<number>,
  idx: Int32Array,
  n: number,
  closed: boolean,
  radius: number,
  seg: number,
  capped: boolean,
  withUv: boolean,
  out: SweepOut | null,
): SweepSize {
  ACC.vertices = 0;
  ACC.triangles = 0;
  if (n < 2 || seg < 3) return { vertices: 0, triangles: 0 };
  ensureVertexScratch(n);
  ensureCircle(seg);

  // Which vertices can carry a shared mitred ring at all.
  for (let i = 0; i < n; i++) {
    tanHalf[i] = 0;
    mitre[i] = 0;
    if (!closed && (i === 0 || i === n - 1)) continue;
    leg(pos, idx, (i - 1 + n) % n, i, A);
    leg(pos, idx, i, (i + 1) % n, B);
    const c = A.dot(B);
    if (c < MITRE_MIN_LEG_DOT) continue;
    // tan(turn/2) = sqrt((1-c)/(1+c)); the guard above keeps c away from -1.
    tanHalf[i] = Math.sqrt(Math.max(0, (1 - c) / (1 + c)));
    mitre[i] = 1;
  }

  // A mitre reaches r*tan(turn/2) into each of its legs. Where the two ends of
  // one leg would reach past each other the strip inverts and folds, so give up
  // the mitre at the sharper end. One forward pass: it can drop a mitre a later
  // relaxation would have saved, but it is the same decision in both modes,
  // which is what matters. Only bites where a short leg meets a sharp bend.
  const legs = closed ? n : n - 1;
  for (let k = 0; k < legs; k++) {
    const i = k;
    const j = (k + 1) % n;
    if (tanHalf[i] === 0 && tanHalf[j] === 0) continue;
    const len = leg(pos, idx, i, j, D);
    if (radius * (tanHalf[i] + tanHalf[j]) <= len) continue;
    const worse = tanHalf[i] >= tanHalf[j] ? i : j;
    mitre[worse] = 0;
    tanHalf[worse] = 0;
  }

  // Visiting order. A break vertex carries two coincident butt rings, so it
  // appears twice; a loop with any break is opened at the first one, which then
  // becomes both ends of a single open walk.
  let orderLen = n;
  let ringsClosed = false;
  if (!closed) {
    for (let i = 0; i < n; i++) order[i] = i;
  } else {
    let cut = -1;
    for (let i = 0; i < n; i++) {
      if (!mitre[i]) {
        cut = i;
        break;
      }
    }
    if (cut < 0) {
      for (let i = 0; i < n; i++) order[i] = i;
      ringsClosed = true;
    } else {
      for (let k = 0; k <= n; k++) order[k] = (cut + k) % n;
      orderLen = n + 1;
    }
  }

  if (ringsClosed) {
    // Parallel transport around a planar loop (every tie and stirrup) returns to
    // its start exactly, so the seam strip closes cleanly. A non-planar loop
    // keeps a small phase shear there, of the same order as the shear already
    // present inside every mitred strip.
    emitRun(pos, idx, order, 0, orderLen - 1, true, radius, seg, capped, withUv, out);
  } else {
    let s = 0;
    for (let j = 1; j < orderLen; j++) {
      const last = j === orderLen - 1;
      if (!last && mitre[order[j]]) continue;
      emitRun(pos, idx, order, s, j, false, radius, seg, capped, withUv, out);
      s = j;
    }
  }

  return { vertices: ACC.vertices, triangles: ACC.triangles };
}
